import axios from "axios"
import normalize from "Utils/normalizer"
import errorHandler from "Utils/error-handler"
import useSession from "Hooks/session.hook"
import { getWithExpiry } from "Helpers/getset-with-expiry"
import { getAccessToken } from "Helpers/session"
import { toLower, omitBy, isEmpty, isNil, map, some } from "lodash"
import { DefaultFilter, Pagination, Sorting } from "./../types/common.types"
import { DriverApprovalStatus, DriverFormInputType } from "./../types/driver_form.types"

const url = `${process.env.API_URL}/${process.env.API_VER}`

interface Filters extends DefaultFilter {
    orFields?: string
}

export interface RiderApprovalInput {
    approvalStatus: "APPROVED" | "REJECTED";
    orgID: string;
    rejectedReason?: string;
}

export const fetchDriverList = async (
    filters: Filters,
    orgID: string,
    approvalStatus?: DriverApprovalStatus,
    onlineStatus?: boolean,
    pagination?: Pagination,
    sorting?: Sorting
) => {
    const accessToken = getAccessToken()

    try {
        const response = await axios({
            method: "POST",
            url: `${url}/org/driver/list`,
            data: omitBy(
                {
                    filters: omitBy(
                        filters,
                        value => isNil(value) || value === ""
                    ),
                    orgID,
                    approvalStatus,
                    onlineStatus,
                    pagination,
                    sorting,
                },
                isNil
            ),
            headers: { Authorization: `Bearer ${accessToken}` },
        })

        return response.data.drivers
    } catch (err: any) {
        const { error } = err.response?.data || {}

        if (!isEmpty(error)) {
            const callback = async () =>
                await fetchDriverList(
                    filters,
                    orgID,
                    approvalStatus,
                    onlineStatus,
                    pagination,
                    sorting
                )
            const { value, code, message, stack } = await errorHandler.response(
                error,
                { callback }
            )
            return { error: { value, code, message, stack } }
        } else {
            console.log(err.message)
            return { error: { message: err.message } }
        }
    }
}

export const fetchDiolkoCompaniesList = async (orgID: string) => {
    const accessToken = getAccessToken()

    try {
        const response = await axios({
            method: "POST",
            url: `${url}/org/company/list`,
            data: omitBy(
                {
                    orgID: orgID,
                },
                isNil
            ),
            headers: { Authorization: `Bearer ${accessToken}` },
        })

        return response.data
    } catch (err: any) {
        const { error } = err.response?.data || {}

        if (!isEmpty(error)) {
            const callback = async () => await fetchDiolkoCompaniesList(orgID)
            const { value, code, message, stack } = await errorHandler.response(
                error,
                { callback }
            )
            return { error: { value, code, message, stack } }
        } else {
            console.log(err.message)
            return { error: { message: err.message } }
        }
    }
}

export const createDriver = async (data:DriverFormInputType) => {
    const accessToken = getAccessToken()
    console.log(normalize.model.driver_management(data));
    try {
        const response = await axios({
            method: "POST",
            url: `${url}/org/driver/create`,
            data: normalize.model.driver_management(data),
            headers: { Authorization: `Bearer ${accessToken}` },
        })

        return response.data
    } catch (err: any) {
        const { error } = err.response?.data || {}

        if (!isEmpty(error)) {
            const callback = async () => await createDriver(data)
            const { value, code, message, stack } = await errorHandler.response(
                error,
                { callback }
            )
            return { error: { value, code, message, stack } }
        } else {
            console.log(err.message)
            return { error: { message: err.message } }
        }
    }
}

export const createExternalDriver = async (data:DriverFormInputType) => {
    const accessToken = getAccessToken()
    console.log(normalize.model.driver_management(data));
    try {
        const response = await axios({
            method: "POST",
            url: `${url}/auth/driver/create`,
            data: normalize.model.driver_management(data),
            headers: { Authorization: `Bearer ${accessToken}` },
        })

        return response.data
    } catch (err: any) {
        const { error } = err.response?.data || {}

        if (!isEmpty(error)) {
            const callback = async () => await createDriver(data)
            const { value, code, message, stack } = await errorHandler.response(
                error,
                { callback }
            )
            return { error: { value, code, message, stack } }
        } else {
            console.log(err.message)
            return { error: { message: err.message } }
        }
    }
}

export const fetchDriver = async (id:string) => {
    const accessToken = getAccessToken();

    try {
        const response = await axios({
            method: "POST",
            url: `${url}/org/driver/fetch`,
            data: {
                profileID:id,
            },
            headers: { Authorization: `Bearer ${accessToken}` },
        })

        return normalize.response.driver_management(response.data);
        // return response.data;
    } catch (err: any) {
        const { error } = err.response?.data || {}

        if (!isEmpty(error)) {
            const callback = async () => await fetchDriver(id)
            const { value, code, message, stack } = await errorHandler.response(
                error,
                { callback }
            )
            return { error: { value, code, message, stack } }
        } else {
            console.log(err.message)
            return { error: { message: err.message } }
        }
    }
}

export const riderApproval = async (data: RiderApprovalInput) => {

    const accessToken = getAccessToken();

    try {
        const response = await axios({
          method: 'POST',
          url: `${url}/org/driver/approval/update`,
          data: data,
          headers: { 'Authorization': `Bearer ${accessToken}` }
        })

        return response.data;
      }
      catch (err: any) {

        const { error } = err.response?.data || {};
    
        if (!isEmpty(error)) {
          const callback = async () => await riderApproval(data)
          const { value, code, message, stack } = await errorHandler.response(error, { callback });
          return { error: { value, code, message, stack } };
        }
        else {
          console.log(err.message)
          return { error: { message: err.message } };
        }
    }
}

export const riderActivation = async (profileID: string, tempDelete: boolean, tempDeletedReason?: string) => {

    const accessToken = getAccessToken();

    try {
        const response = await axios({
          method: 'POST',
          url: `${url}/org/driver/activation/update`,
          data: {profileID, tempDelete, tempDeletedReason},
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });

        return response.data;
      }
      catch (err: any) {
        const { error } = err.response?.data || {};
    
        if (!isEmpty(error)) {
          const callback = async () => await riderActivation(profileID, tempDelete, tempDeletedReason)
          const { value, code, message, stack } = await errorHandler.response(error, { callback });
          return { error: { value, code, message, stack } };
        }
        else {
          console.log(err.message)
          return { error: { message: err.message } };
        }
    }
}

export const fetchDriverStatisticCount = async (orgID: [], approvalStatus?: DriverApprovalStatus, onlineStatus?: boolean) => {

    const accessToken = getAccessToken();

    try {
        const response = await axios({
          method: 'POST',
          url: `${url}/org/driver/statistics/list`,
          data: {orgID, approvalStatus, onlineStatus},
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });

        return response.data;
      }
      catch (err: any) {
        const { error } = err.response?.data || {};
    
        if (!isEmpty(error)) {
          const callback = async () => await fetchDriverStatisticCount(orgID, approvalStatus, onlineStatus)
          const { value, code, message, stack } = await errorHandler.response(error, { callback });
          return { error: { value, code, message, stack } };
        }
        else {
          console.log(err.message)
          return { error: { message: err.message } };
        }
    }
}

export const updateDriver = async (data) => {

    const accessToken = getAccessToken();

    try {
        const response = await axios({
          method: 'POST',
          url: `${url}/org/driver/update`,
          data: normalize.model.driver_update(data),
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });

        return response.data;
      }
      catch (err: any) {
        const { error } = err.response?.data || {};
    
        if (!isEmpty(error)) {
          const callback = async () => await updateDriver(data)
          const { value, code, message, stack } = await errorHandler.response(error, { callback });
          return { error: { value, code, message, stack } };
        }
        else {
          console.log(err.message)
          return { error: { message: err.message } };
        }
    }
}

export const fetchDeletedDriverList = async (
    filters: Filters,
    orgID: string,
    approvalStatus: DriverApprovalStatus,
    tempDelete,
    // onlineStatus?: boolean,
    pagination?: Pagination,
    sorting?: Sorting
) => {

    const accessToken = getAccessToken();

    try {
        const response = await axios({
          method: 'POST',
          url: `${url}/org/driver/list`,
          data: omitBy(
            {
                filters: omitBy(
                    filters,
                    value => isNil(value) || value === ""
                ),
                orgID,
                approvalStatus:approvalStatus,
                tempDelete:tempDelete,
                pagination,
                sorting,
            },
            isNil
        ),
          headers: { 'Authorization': `Bearer ${accessToken}` }
        });

        return response.data.drivers;
      }
      catch (err: any) {
        const { error } = err.response?.data || {};
    
        if (!isEmpty(error)) {
          const callback = async () => await fetchDeletedDriverList(filters,
            orgID,
            approvalStatus,
            tempDelete,
            pagination,
            sorting)
          const { value, code, message, stack } = await errorHandler.response(error, { callback });
          return { error: { value, code, message, stack } };
        }
        else {
          console.log(err.message)
          return { error: { message: err.message } };
        }
    }
}

